import { Link } from "@tanstack/react-router";
import { REGIMEN_FISCAL } from "@uirtus/shared";
import { useState } from "react";
import { FormularioCliente } from "../componentes/formulario-cliente";
import { Aviso, Boton, Entrada, Etiqueta, Modal, Seleccion } from "../componentes/ui";
import { ErrorApi } from "../lib/api";
import { useClientes, useCrearCliente } from "../lib/clientes";
import { useDebounce } from "../lib/use-debounce";

type Filtro = "todos" | "activos" | "inactivos";

const A_BOOLEANO: Record<Filtro, boolean | undefined> = {
  todos: undefined,
  activos: true,
  inactivos: false,
};

export function Clientes() {
  const [texto, setTexto] = useState("");
  const [filtro, setFiltro] = useState<Filtro>("activos");
  const [abierto, setAbierto] = useState(false);

  // Sin esto se dispara una consulta por cada tecla.
  const busqueda = useDebounce(texto, 300);

  const { data, isLoading, isError, error } = useClientes({
    q: busqueda || undefined,
    activo: A_BOOLEANO[filtro],
  });

  const crear = useCrearCliente();

  const clientes = data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-lg font-semibold">Clientes</h1>
        <Boton onClick={() => setAbierto(true)}>Nuevo cliente</Boton>
      </div>

      <div className="flex flex-wrap gap-2">
        <Entrada
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Buscar por RFC, razón social o nombre comercial"
          className="w-full sm:w-96"
        />
        <Seleccion
          value={filtro}
          onChange={(e) => setFiltro(e.target.value as Filtro)}
          className="w-full sm:w-40"
        >
          <option value="activos">Activos</option>
          <option value="inactivos">Inactivos</option>
          <option value="todos">Todos</option>
        </Seleccion>
      </div>

      {isError && (
        <Aviso>
          {error instanceof ErrorApi ? error.message : "No se pudo cargar la lista"}
        </Aviso>
      )}

      {/* Con error no se pinta el estado vacío: decir "todavía no hay clientes"
          cuando en realidad no se pudo consultar induce a dar de alta uno que
          quizá ya existe. */}
      {isLoading ? (
        <p className="text-sm text-slate-500">Cargando…</p>
      ) : isError ? null : clientes.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 px-4 py-12 text-center dark:border-slate-700">
          <p className="text-sm text-slate-500">
            {busqueda
              ? `Ningún cliente coincide con "${busqueda}".`
              : filtro === "inactivos"
                ? "No hay clientes desactivados."
                : "Todavía no hay clientes."}
          </p>
          {!busqueda && filtro !== "inactivos" && (
            <Boton className="mt-4" onClick={() => setAbierto(true)}>
              Dar de alta el primero
            </Boton>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 text-left text-xs uppercase text-slate-500 dark:border-slate-800">
              <tr>
                <th className="px-4 py-2">RFC</th>
                <th className="px-4 py-2">Cliente</th>
                <th className="px-4 py-2">Régimen</th>
                <th className="px-4 py-2">CP</th>
                <th className="px-4 py-2">Crédito</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {clientes.map((c) => (
                <tr
                  key={c.id}
                  className="transition hover:bg-slate-50 dark:hover:bg-slate-800/50"
                >
                  <td className="px-4 py-2 font-mono text-xs">{c.rfc}</td>
                  <td className="px-4 py-2">
                    <Link
                      to="/clientes/$id"
                      params={{ id: c.id }}
                      className="font-medium text-marca-600 hover:underline dark:text-marca-500"
                    >
                      {c.razonSocial}
                    </Link>
                    {c.nombreComercial && (
                      <div className="text-xs text-slate-500">{c.nombreComercial}</div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-500">
                    {c.regimenFiscal} · {REGIMEN_FISCAL[c.regimenFiscal]}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{c.codigoPostal}</td>
                  <td className="px-4 py-2 text-xs">
                    {c.diasCredito > 0 ? `${c.diasCredito} días` : "Contado"}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex justify-end gap-1">
                      {!c.activo && <Etiqueta>Inactivo</Etiqueta>}
                      {c.enListaNegra && <Etiqueta tono="peligro">69-B</Etiqueta>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        titulo="Nuevo cliente"
        abierto={abierto}
        alCerrar={() => {
          setAbierto(false);
          crear.reset();
        }}
      >
        <FormularioCliente
          enviando={crear.isPending}
          errorServidor={crear.error ? crear.error.message : null}
          alEnviar={(datos) =>
            crear.mutate(datos as never, {
              onSuccess: () => {
                setAbierto(false);
                crear.reset();
              },
            })
          }
          alCancelar={() => {
            setAbierto(false);
            crear.reset();
          }}
        />
      </Modal>
    </div>
  );
}
